import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { getSheets, getSheetThumbnailUrl } from '../utils/api';
import styles from './FilePickerModal.module.css';

// Single-select sheet picker. Sheets aren't in a folder tree like Files —
// they're a flat per-project list — so the layout is simpler: a search
// box at the top filters by sheet_number / title, and the contents pane
// shows each match as a thumbnail + label row. The currently-active
// sheet is highlighted (and excluded from the picker — you can't link a
// markup to its own parent sheet).
//
// API mirrors FilePickerModal: `{ onClose, onSelect, title, excludeId }`
// where onSelect receives the sheet row when the user confirms.

export default function SheetPickerModal({ onClose, onSelect, title = 'Pick a sheet', excludeId = null }) {
  const { project } = useStore();

  const [sheets, setSheets]               = useState([]);
  const [selectedSheetId, setSelectedSheetId] = useState(null);
  const [search, setSearch]               = useState('');
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    if (!project) return;
    let alive = true;
    getSheets({ project_id: project.id })
      .then((list) => { if (alive) { setSheets(list); setLoading(false); } })
      .catch((err) => { if (alive) { toast.error(err.message); setLoading(false); } });
    return () => { alive = false; };
  }, [project?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = excludeId ? sheets.filter((s) => s.id !== excludeId) : sheets;
    if (!q) return base;
    return base.filter((s) =>
      (s.sheet_number || '').toLowerCase().includes(q)
      || (s.title || '').toLowerCase().includes(q)
      || (s.discipline_name || '').toLowerCase().includes(q)
    );
  }, [sheets, search, excludeId]);

  const selectedSheet = filtered.find((s) => s.id === selectedSheetId) || null;

  function handleConfirm() {
    if (!selectedSheet) return;
    onSelect?.(selectedSheet);
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span>{title}</span>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.body}>
          <main className={styles.main}>
            <div className={styles.crumbs}>
              <input
                type="search"
                placeholder="Search by sheet # / title / discipline…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                style={{
                  flex: 1,
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '6px 10px',
                  color: 'var(--text)',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
            </div>

            <div className={styles.listWrap}>
              {loading && <div className={styles.empty}>Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div className={styles.empty}>
                  {search ? 'No sheets match.' : 'No sheets in this project.'}
                </div>
              )}

              {filtered.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionLabel}>Sheets</div>
                  <div className={styles.fileList}>
                    {filtered.map((s) => (
                      <button
                        key={s.id}
                        className={`${styles.fileRow} ${selectedSheetId === s.id ? styles.fileRowActive : ''}`}
                        onClick={() => setSelectedSheetId(s.id)}
                        onDoubleClick={() => { setSelectedSheetId(s.id); onSelect?.(s); }}
                      >
                        <img
                          src={getSheetThumbnailUrl(s.id, 80)}
                          alt={s.sheet_number}
                          style={{
                            width: 56, height: 40, objectFit: 'contain',
                            background: 'var(--surface3)',
                            border: '1px solid var(--border)',
                            borderRadius: 2,
                            flexShrink: 0,
                          }}
                        />
                        <div className={styles.fileNameStack}>
                          <span className={styles.fileName}>
                            {s.sheet_number}{s.title && s.title !== s.sheet_number ? ` — ${s.title}` : ''}
                          </span>
                          <span className={styles.fileMeta}>
                            {s.discipline_name || s.discipline_designator || '—'}
                            {s.version_set_name && ` · ${s.version_set_name}`}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {selectedSheet
              ? <>Selected: <strong>{selectedSheet.sheet_number}</strong></>
              : <>Pick a sheet from the list</>}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
            <button
              className={styles.btnPrimary}
              onClick={handleConfirm}
              disabled={!selectedSheet}
            >Select</button>
          </div>
        </div>
      </div>
    </div>
  );
}
